import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import GroupIcon from '@mui/icons-material/Group';
import hljs from 'highlight.js/lib/common';
import 'highlight.js/styles/github-dark-dimmed.css';

import { Code, CodeBox, CodeHeadline } from '../../styled/CodePanel.styled';
import { FullHeightCard } from '../../styled/Chat.styled';

import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { escapeHtml } from '../../utils/escapeHtml';

interface CodePanelProps {
  label: string;
  code: string | null;
  onChangeRequestClick: (() => void) | null;
}

const CodePanel = ({ label, code, onChangeRequestClick }: CodePanelProps) => {
  const location = useLocation();

  const codeRef = useRef<HTMLDivElement>(null);
  const [, copyToClipboard] = useCopyToClipboard();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (codeRef.current && code) {
      codeRef.current.innerHTML = escapeHtml(code);
      codeRef.current.removeAttribute('data-highlighted');
      hljs.highlightElement(codeRef.current);
    }
  }, [code]);

  const handleCopy = useCallback(async () => {
    if (!code) return;

    const isCopied = await copyToClipboard(code);
    setCopied(isCopied);
  }, [code, copyToClipboard]);

  useEffect(() => {
    if (!copied) return;

    const timer = setTimeout(() => setCopied(false), 1000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <FullHeightCard>
      <CodeBox>
        <CodeHeadline>
          <Typography fontWeight="bold">{label}</Typography>
          <Stack flexDirection="row" gap={1}>
            {onChangeRequestClick && (
              <IconButton onClick={onChangeRequestClick}>
                <GroupIcon />
              </IconButton>
            )}
            <Tooltip title={copied ? 'Скопійовано' : 'Скопіювати'}>
              <div>
                <IconButton onClick={handleCopy} disabled={!code || copied}>
                  {copied ? <CheckIcon /> : <ContentCopyIcon />}
                </IconButton>
              </div>
            </Tooltip>
          </Stack>
        </CodeHeadline>
        <Code key={location.key}>
          {code && (
            <pre>
              <code ref={codeRef} />
            </pre>
          )}
        </Code>
      </CodeBox>
    </FullHeightCard>
  );
};

export default CodePanel;
